'use client'

import React from 'react'
import { useMediaQuery } from 'react-responsive'
import { Picture } from '../../components/index.js'
import type { Media as MediaType } from '../../types/props.js'
import { cn } from '../../helpers/index.js'

export interface MediaProps extends React.HTMLAttributes<HTMLDivElement> {
  /** PC端媒体 */
  pcImage?: MediaType
  /** 平板端媒体 */
  padImage?: MediaType
  /** 移动端媒体 */
  mobileImage?: MediaType
  /** 视频类名 */
  videoClassName?: string
  /** 图片类名 */
  imgClassName?: string
  /** 是否静音播放（仅视频） */
  muted?: boolean
  /** 是否循环播放（仅视频） */
  loop?: boolean
  /** 是否内联播放（仅视频） */
  playsInline?: boolean
  /** 是否自动播放（仅视频） */
  autoPlay?: boolean
  /** 视频预览图 */
  poster?: string
}

/**
 * Media - 智能媒体组件
 *
 * @description 根据媒体类型自动选择使用 video 标签或 Picture 组件
 * 支持响应式媒体源，如果任意媒体类型是视频，则使用 video 标签渲染
 */
const Media = React.forwardRef<HTMLDivElement, MediaProps>(
  (
    {
      pcImage,
      padImage,
      mobileImage,
      className,
      videoClassName,
      imgClassName,
      muted = true,
      loop = true,
      playsInline = true,
      autoPlay = true,
      poster,
      ...props
    },
    ref
  ) => {
    // 响应式查询
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' })
    const isPad = useMediaQuery({ query: '(max-width: 1024px)' })

    // 检查是否有任何媒体是视频类型
    const hasVideo = [pcImage, padImage, mobileImage].some(media => media?.mimeType === 'video/mp4')

    // 获取当前设备对应的媒体
    const currentMedia = isMobile ? mobileImage : isPad ? padImage || mobileImage : pcImage

    // 获取视频源和海报图片
    const videoSrc = currentMedia?.mimeType === 'video/mp4' ? currentMedia.url : undefined
    const imagePoster = poster || (currentMedia?.mimeType !== 'video/mp4' ? currentMedia?.url : undefined)

    return (
      <div ref={ref} className={cn('media-wrapper', className)} {...props}>
        {hasVideo ? (
          <video
            src={videoSrc}
            poster={imagePoster}
            className={cn('size-full object-cover', videoClassName)}
            muted={muted}
            loop={loop}
            playsInline={playsInline}
            autoPlay={autoPlay}
            aria-label={currentMedia?.alt || ''}
          />
        ) : (
          <Picture
            className="size-full"
            imgClassName={cn('size-full object-cover', imgClassName)}
            alt={pcImage?.alt || ''}
            source={`${pcImage?.url || ''} , ${padImage?.url ?? (mobileImage?.url || '')} 1024, ${mobileImage?.url || ''} 767`}
          />
        )}
      </div>
    )
  }
)

Media.displayName = 'Media'

export default Media
